import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Mail } from "lucide-react";
import { api, errMsg } from "../lib";
import { Field, useHuman, Notice } from "../ui";

const GREG = "greg@eversoul.world";
const PAPERS = ["Smooth matte", "Textured fine art paper"];
const blank = { name: "", email: "", size: "Please advise", custom_width: "", custom_height: "", custom_unit: "in", paper: "Please advise",
  framing: "", quantity: 1, city: "", region: "", postal_code: "", country: "", message: "" };

const Select = ({ id, label, value, onChange, options, testid }) => (
  <div><label className="lou-label" htmlFor={id}>{label}</label>
    <select id={id} className="lou-input" value={value} onChange={onChange} data-testid={testid}>
      {options.map(([v, t]) => <option key={v} value={v}>{t}</option>)}</select></div>
);

function Form({ art, image, onDone }) {
  const human = useHuman();
  const [f, setF] = useState(blank);
  const [state, setState] = useState({ sending: false, error: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const sizes = [...(art.print_options || []), "Custom", "Please advise"];
  const papers = [...new Set([...(art.paper_options || []), ...PAPERS])];
  const submit = async (e) => {
    e.preventDefault();
    setState({ sending: true, error: "" });
    try {
      await api.post("/submit/print-inquiry", { ...f, quantity: Number(f.quantity) || 1, artwork_id: art.id,
        custom_unit: f.size === "Custom" ? f.custom_unit : "", page_url: window.location.href.split("?")[0], ...human.values });
      onDone();
    } catch (err) { setState({ sending: false, error: errMsg(err) }); human.reset(); }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="print-inquiry-form">
      <div className="flex gap-4 items-center border-b border-lou-line pb-5">
        {image && <img src={image} alt="" className="w-20 h-20 object-contain bg-lou-line/40 shrink-0" data-testid="print-inquiry-image" />}
        <div><p className="text-xs uppercase tracking-[0.2em] text-lou-clay">Print inquiry · <span className="whitespace-nowrap">{art.ref}</span></p>
          <p className="font-lou text-2xl" data-testid="print-inquiry-title">{art.title || "Untitled"}</p></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field id="pi-name" label="Name" required value={f.name} onChange={set("name")} data-testid="print-name" />
        <Field id="pi-email" label="Email address" type="email" required value={f.email} onChange={set("email")} data-testid="print-email" />
        <Select id="pi-size" label="Preferred print size" value={f.size} onChange={set("size")} testid="print-size"
          options={sizes.map((s) => [s, s === "Custom" ? "Custom dimensions" : s])} />
        <Select id="pi-paper" label="Preferred paper or finish" value={f.paper} onChange={set("paper")} testid="print-paper"
          options={[...papers, "Please advise"].map((p) => [p, p])} />
      </div>
      {f.size === "Custom" && (
        <div className="grid grid-cols-3 gap-4" data-testid="print-custom-size">
          <Field id="pi-w" label="Width" inputMode="decimal" required value={f.custom_width} onChange={set("custom_width")} data-testid="print-custom-width" />
          <Field id="pi-h" label="Height" inputMode="decimal" required value={f.custom_height} onChange={set("custom_height")} data-testid="print-custom-height" />
          <Select id="pi-unit" label="Units" value={f.custom_unit} onChange={set("custom_unit")} testid="print-custom-unit" options={[["in", "inches"], ["cm", "centimeters"]]} />
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        <Select id="pi-frame" label="Framing preference" value={f.framing} onChange={set("framing")} testid="print-framing"
          options={[["", "Choose…"], ["unframed", "Unframed"], ["interested", "Interested in framing"], ["undecided", "Undecided"]]} />
        <Field id="pi-qty" label="Quantity" type="number" min={1} max={50} value={f.quantity} onChange={set("quantity")} data-testid="print-quantity" />
      </div>
      <fieldset><legend className="lou-label">Delivery location</legend>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field id="pi-city" label="City" value={f.city} onChange={set("city")} data-testid="print-city" />
          <Field id="pi-region" label="State or region" value={f.region} onChange={set("region")} data-testid="print-region" />
          <Field id="pi-postal" label="Postal code" value={f.postal_code} onChange={set("postal_code")} data-testid="print-postal" />
          <Field id="pi-country" label="Country" value={f.country} onChange={set("country")} data-testid="print-country" />
        </div></fieldset>
      <Field id="pi-msg" label="Additional questions or requests" textarea value={f.message} onChange={set("message")} data-testid="print-message" />
      <p className="text-xs text-lou-soft">Sizes, papers and framing are your preferences only. Greg will confirm what is possible for this painting. Your contact and delivery details stay private.</p>
      {human.ui}
      <Notice testid="print-inquiry-notice">Greg will contact you to discuss available sizes, paper options, framing, pricing, and delivery. Submitting an inquiry does not place an order or require payment.</Notice>
      {state.error && <Notice kind="error" testid="print-inquiry-error">{state.error}</Notice>}
      <div className="flex flex-wrap items-center gap-5">
        <button className="lou-btn" disabled={state.sending} data-testid="print-inquiry-submit">{state.sending ? "Sending…" : "Send print inquiry"}</button>
        <a href={`mailto:${GREG}?subject=${encodeURIComponent(`Print inquiry: ${art.title || "Untitled"} (${art.ref})`)}`}
          className="text-sm inline-flex items-center gap-2 hover:text-lou-clay" data-testid="print-inquiry-email-link"><Mail size={15} /> Or email {GREG}</a>
      </div>
    </form>
  );
}

export default function PrintInquiryDialog({ art, image, onClose }) {
  const [done, setDone] = useState(false);
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", esc); };
  }, [onClose]);
  return createPortal(
    <div className="lou-root fixed inset-0 z-[60] bg-lou-ink/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto"
      role="dialog" aria-modal="true" aria-label="Request a print" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="print-inquiry-dialog">
      <div className="bg-lou-ivory w-full max-w-2xl p-6 sm:p-10 relative my-6 lou-fade-in">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:text-lou-clay" aria-label="Close" data-testid="print-inquiry-close"><X /></button>
        {done ? (
          <div className="py-10 text-center" data-testid="print-inquiry-success">
            <p className="font-lou text-3xl font-light">Thank you</p>
            <p className="mt-4 text-lou-soft leading-relaxed max-w-md mx-auto">Thank you for your interest in Louise’s artwork. Your print inquiry has been received. Greg will contact you to discuss the details.</p>
            <button className="lou-btn-ghost mt-8" onClick={onClose} data-testid="print-inquiry-done">Close</button>
          </div>
        ) : <Form art={art} image={image} onDone={() => setDone(true)} />}
      </div>
    </div>,
    document.body
  );
}
